import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs, ManipulateType } from "dayjs";
import { SafeDatePicker } from "components/SafeDatePicker";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useMemo, useState } from "react";
import { TimeRangeFilterMode } from "src/store/api/generatedApi";

const RANGE_PRESETS: { id: string; amount: number; unit: ManipulateType }[] = [
  { id: "1week", amount: 1, unit: "week" },
  { id: "1month", amount: 1, unit: "month" },
  { id: "3months", amount: 3, unit: "month" },
];

const detectPreset = (start: Dayjs | null, end: Dayjs | null): string => {
  if (start === null && end === null) return "allTime";
  if (start === null || end === null) return "";
  for (const preset of RANGE_PRESETS) {
    const expectedStart = end.subtract(preset.amount, preset.unit);
    if (Math.abs(start.diff(expectedStart, "day")) <= 1) return preset.id;
  }
  return "";
};

export interface DateRangeFilterState {
  start: Dayjs | null;
  end: Dayjs | null;
  filterMode: TimeRangeFilterMode;
}

export const useDateRangeFilters = () => {
  const [start, setStart] = useState<Dayjs | null>(null);
  const [end, setEnd] = useState<Dayjs | null>(null);
  const [filterMode, setFilterMode] =
    useState<TimeRangeFilterMode>("label_time");

  const handleStartChange = useCallback((value: Dayjs | null) => {
    if (value === null || value.isValid()) setStart(value?.utc(true) ?? null);
  }, []);

  const handleEndChange = useCallback((value: Dayjs | null) => {
    if (value === null || value.isValid()) setEnd(value?.utc(true) ?? null);
  }, []);

  const handlePresetChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, presetId: string | null) => {
      if (!presetId) return;
      if (presetId === "allTime") {
        setStart(null);
        setEnd(null);
        return;
      }
      const preset = RANGE_PRESETS.find((p) => p.id === presetId);
      if (!preset) return;
      const now = dayjs().utc(true);
      setStart(now.subtract(preset.amount, preset.unit));
      setEnd(now);
    },
    []
  );

  const handleFilterModeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, mode: string | null) => {
      if (mode === "label_time" || mode === "real_time") setFilterMode(mode);
    },
    []
  );

  const currentPreset = useMemo(() => detectPreset(start, end), [start, end]);

  return {
    start,
    end,
    filterMode,
    currentPreset,
    handleStartChange,
    handleEndChange,
    handlePresetChange,
    handleFilterModeChange,
  };
};

export const DateRangeFilterBar = ({
  filters,
}: {
  filters: ReturnType<typeof useDateRangeFilters>;
}) => {
  const { t } = useTenantTranslation();

  return (
    <Stack
      direction="row"
      spacing={2}
      useFlexGap
      alignItems="center"
      flexWrap="wrap"
    >
      <SafeDatePicker
        label={t("startNoun")}
        value={filters.start}
        onChange={filters.handleStartChange}
        slotProps={{
          textField: { size: "small" },
          field: { clearable: true },
        }}
        format="YYYY-MM-DD"
      />
      <Typography variant="body2" color="text.secondary">
        –
      </Typography>
      <SafeDatePicker
        label={t("endNoun")}
        value={filters.end}
        onChange={filters.handleEndChange}
        slotProps={{
          textField: { size: "small" },
          field: { clearable: true },
        }}
        format="YYYY-MM-DD"
      />
      <ToggleButtonGroup
        size="small"
        value={filters.currentPreset}
        exclusive
        onChange={filters.handlePresetChange}
        sx={{
          "& .MuiToggleButton-root": {
            px: 1.5,
            py: 0.5,
            textTransform: "none",
          },
        }}
      >
        <ToggleButton value="allTime">{t("allTime")}</ToggleButton>
        {RANGE_PRESETS.map((preset) => (
          <ToggleButton key={preset.id} value={preset.id}>
            {t(preset.id)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <ToggleButtonGroup
        size="small"
        value={filters.filterMode}
        exclusive
        onChange={filters.handleFilterModeChange}
        sx={{
          "& .MuiToggleButton-root": {
            px: 1.5,
            py: 0.5,
            textTransform: "none",
          },
        }}
      >
        <ToggleButton value="label_time">{t("filterByLabelTime")}</ToggleButton>
        <ToggleButton value="real_time">{t("filterByRealTime")}</ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};
